import React from "react";
import { renderTextWithPlaceholders as rT } from "./PlaceholderToken";

export default function ResumePreviewProfessional({ data, editable = false, onPlaceholderFill }) {
  const c = data?.contact || {};
  const onFill = editable ? onPlaceholderFill : undefined;

  const contactFields = [
    { val: c.email,    path: "contact.email"    },
    { val: c.phone,    path: "contact.phone"    },
    { val: c.location, path: "contact.location" },
    { val: c.linkedin, path: "contact.linkedin" },
  ].filter(x => x.val);

  return (
    <div className="bg-white font-sans text-[#1a1a1a] text-[13px] leading-relaxed max-w-[750px] mx-auto shadow-sm">
      {/* Header band */}
      <div className="bg-[#1D4ED8] text-white px-8 py-6">
        {c.name && (
          <h1 className="text-[26px] font-bold tracking-wide mb-1">
            {rT(c.name, "contact.name", editable, onFill)}
          </h1>
        )}
        {contactFields.length > 0 && (
          <p className="text-[11px] text-blue-100">
            {contactFields.map((x, i) => (
              <React.Fragment key={x.path}>
                {i > 0 && "  ·  "}
                {rT(x.val, x.path, editable, onFill)}
              </React.Fragment>
            ))}
          </p>
        )}
      </div>

      <div className="px-8 py-6">
        {data?.summary && (
          <Section title="Professional Summary" accent>
            <p className="text-[#333]">{rT(data.summary, "summary", editable, onFill)}</p>
          </Section>
        )}

        {data?.experience?.length > 0 && (
          <Section title="Work Experience" accent>
            {data.experience.map((exp, i) => (
              <div key={i} className="mb-5">
                <div className="flex justify-between items-baseline gap-4">
                  <span className="font-bold text-[14px] text-[#1D4ED8]">
                    {rT(exp.title, `experience.${i}.title`, editable, onFill)}
                  </span>
                  <span className="text-[11px] text-[#666] whitespace-nowrap">
                    {rT(exp.start_date, `experience.${i}.start_date`, editable, onFill)}
                    {" – "}
                    {rT(exp.end_date, `experience.${i}.end_date`, editable, onFill)}
                  </span>
                </div>
                <div className="font-semibold text-[12px] text-[#444] mb-1">
                  {rT(exp.company, `experience.${i}.company`, editable, onFill)}
                  {exp.location && (
                    <>{" · "}{rT(exp.location, `experience.${i}.location`, editable, onFill)}</>
                  )}
                </div>
                <ul className="list-disc list-outside ml-4 space-y-0.5 text-[#333]">
                  {(exp.bullets || []).map((b, j) => (
                    <li key={j}>{rT(b, `experience.${i}.bullets.${j}`, editable, onFill)}</li>
                  ))}
                </ul>
              </div>
            ))}
          </Section>
        )}

        <div className="grid grid-cols-2 gap-6">
          <div>
            {data?.education?.length > 0 && (
              <Section title="Education" accent>
                {data.education.map((edu, i) => (
                  <div key={i} className="mb-3">
                    <div className="font-bold text-[#1D4ED8]">
                      {rT(edu.degree, `education.${i}.degree`, editable, onFill)}
                    </div>
                    <div className="text-[11px] text-[#555]">
                      {rT(edu.institution, `education.${i}.institution`, editable, onFill)}
                    </div>
                    <div className="text-[11px] text-[#777]">
                      {rT(edu.graduation_date, `education.${i}.graduation_date`, editable, onFill)}
                      {edu.location && (
                        <>{" · "}{rT(edu.location, `education.${i}.location`, editable, onFill)}</>
                      )}
                    </div>
                    {(edu.details || []).map((d, j) => (
                      <div key={j} className="text-[11px] text-[#666]">
                        {rT(d, `education.${i}.details.${j}`, editable, onFill)}
                      </div>
                    ))}
                  </div>
                ))}
              </Section>
            )}

            {data?.certifications?.length > 0 && (
              <Section title="Certifications" accent>
                {data.certifications.map((cert, i) => (
                  <div key={i} className="mb-1.5">
                    <div className="font-semibold text-[12px]">
                      {rT(cert.name, `certifications.${i}.name`, editable, onFill)}
                    </div>
                    {(cert.issuer || cert.date) && (
                      <div className="text-[11px] text-[#666]">
                        {cert.issuer && rT(cert.issuer, `certifications.${i}.issuer`, editable, onFill)}
                        {cert.issuer && cert.date && " · "}
                        {cert.date && rT(cert.date, `certifications.${i}.date`, editable, onFill)}
                      </div>
                    )}
                  </div>
                ))}
              </Section>
            )}
          </div>

          <div>
            {data?.skills?.length > 0 && (
              <Section title="Skills" accent>
                {data.skills.map((sk, i) => (
                  <div key={i} className="mb-2">
                    <div className="font-bold text-[11px] text-[#1D4ED8] uppercase tracking-wider">
                      {rT(sk.name, `skills.${i}.name`, editable, onFill)}
                    </div>
                    <div className="text-[#333]">
                      {(sk.items || []).map((item, j) => (
                        <React.Fragment key={j}>
                          {j > 0 && " · "}
                          {rT(item, `skills.${i}.items.${j}`, editable, onFill)}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </Section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, accent, children }) {
  return (
    <div className="mb-5">
      <div className={`font-bold uppercase text-[10px] tracking-widest mb-2 pb-0.5 ${accent ? "border-b-2 border-[#1D4ED8] text-[#1D4ED8]" : "border-b border-[#ccc]"}`}>
        {title}
      </div>
      {children}
    </div>
  );
}
